import { BoardNodes } from 'types/BoardNode';
import {
  FIFTY_PERCENT_PADDING,
  NODE_MIN_HEIGHT,
  NODE_MIN_WIDTH,
} from './constants';
import { useReactFlow, type Node } from 'reactflow';
import CustomNodeWrapper from './CustomNodeWrapper';
import React from 'react';
import { BoardEvent } from 'types/common/BoardEvents';
import { parseJSONCleaned } from 'utilities/index';
import { useBoardActions } from 'hooks/EntityTypes/useBoardActions';
import { useBoardEntityTypes } from 'hooks/EntityTypes/useBoardEntityTypes';
import { useSparkBoardContext } from 'hooks/common/useSparkBoardContext';
import { useBoardNodeByID } from 'hooks/chats/useBoardNodeByID';
import { useBoardNodesContext } from 'hooks/common/useBoardNodesContext';
type NodePosition = { x: number; y: number; width: number; height: number };

export type NodeDataType = {
  boardNode: BoardNodes;
};

type ExtendedBoardNode = Record<
  keyof BoardNodes['attributes'],
  { old: string; new: string }
>;

export const nodeTypes = {
  editorJS: CustomNodeWrapper,
};

export const areNodesTouching = (
  nodeA: NodePosition,
  nodeB: NodePosition
): boolean => {
  return (
    nodeA.x < nodeB.x + nodeB.width &&
    nodeA.x + nodeA.width > nodeB.x &&
    nodeA.y < nodeB.y + nodeB.height &&
    nodeA.y + nodeA.height > nodeB.y
  );
};

export const getNodePosition = (node: Node): NodePosition => ({
  x: node.position.x,
  y: node.position.y,
  width: node.width || NODE_MIN_WIDTH,
  height: node.height || NODE_MIN_HEIGHT,
});

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const isIntersecting = (rect1: Rect, rect2: Rect): boolean => {
  return !(
    (
      rect1.x + rect1.width <= rect2.x || // rect1 is completely left of rect2
      rect1.x >= rect2.x + rect2.width || // rect1 is completely right of rect2
      rect1.y + rect1.height <= rect2.y || // rect1 is completely above rect2
      rect1.y >= rect2.y + rect2.height
    ) // rect1 is completely below rect2
  );
};

const getBoundingRect = (nodeID: string) =>
  document
    .querySelectorAll(`[data-id='${nodeID}']`)[0]
    ?.getBoundingClientRect();

const isSafePosition = (
  position: { x: number; y: number },
  newNode: Node,
  nodes: Node[]
): boolean => {
  const bounds = getBoundingRect(newNode.id);

  const newNodeRect: Rect = {
    x: position.x,
    y: position.y,
    width: bounds?.width || NODE_MIN_WIDTH,
    height: bounds?.height || NODE_MIN_HEIGHT,
  };

  for (const node of nodes) {
    if (node.id === newNode.id) continue;
    const existingNodeBound = getBoundingRect(node.id);
    const existingNodeRect: Rect = {
      x: node.position.x,
      y: node.position.y,
      width: existingNodeBound?.width || NODE_MIN_WIDTH,
      height: existingNodeBound?.height || NODE_MIN_HEIGHT,
    };

    if (isIntersecting(newNodeRect, existingNodeRect)) {
      console.warn('Intersection detected with node:', node.id);
      return false;
    }
  }

  return true;
};

export const findSafePosition = (
  newNode: Node,
  existingNodes: Node[],
  gridSize = 20, // Grid step size
  maxIterations = 1000 // Prevent infinite loops
): Node => {
  const originalPosition = { ...newNode.position };

  let directions = [
    { dx: 0, dy: -1 }, // Up
    { dx: 1, dy: 0 }, // Right
    { dx: 0, dy: 1 }, // Down
    { dx: -1, dy: 0 }, // Left
    { dx: 1, dy: -1 }, // Up-Right
    { dx: 1, dy: 1 }, // Down-Right
    { dx: -1, dy: 1 }, // Down-Left
    { dx: -1, dy: -1 }, // Up-Left
  ];

  let safePosition = { ...originalPosition };
  let step = 1;
  let iteration = 0;

  // Check if original position is safe
  if (isSafePosition(safePosition, newNode, existingNodes)) {
    return { ...newNode, position: safePosition };
  }

  // Expand search in all directions
  while (iteration < maxIterations) {
    for (let i = 0; i < directions.length; i++) {
      const newX = originalPosition.x + directions[i].dx * step * gridSize;
      const newY = originalPosition.y + directions[i].dy * step * gridSize;

      const testPosition = { x: newX, y: newY };

      if (isSafePosition(testPosition, newNode, existingNodes)) {
        safePosition = testPosition;
        return { ...newNode, position: safePosition };
      }
    }

    step++; // Increase the search radius
    iteration++;
  }

  // Return the original position if no safe spot is found
  return { ...newNode, position: originalPosition };
};

export const transformSparkBoardNodeToRectFlowNodes = (
  sparkNode: BoardNodes,
  isDraggable: boolean = true
): Node => {
  return {
    id: sparkNode.id,
    type: 'editorJS',
    data: {
      boardNode: sparkNode,
      draggable: isDraggable,
    },
    position: {
      x: sparkNode.attributes.positionX || -250,
      y: sparkNode.attributes.positionY || -25,
    },
    draggable: isDraggable,
    width: NODE_MIN_WIDTH,
    height: NODE_MIN_HEIGHT,
    dragHandle: '.drag-handle',
  };
};

export const transformAllSparkBoardNodesToRectFlowNodes = (
  sparkNodes: BoardNodes[]
) => {
  return sparkNodes.map((sparkNode) =>
    transformSparkBoardNodeToRectFlowNodes(sparkNode)
  );
};

export const useReactFlowHelpers = () => {
  const { nodes, setNodes, onNodesChange } = useSparkBoardContext();
  const { mutateAsync: getBoardNodeByID } = useBoardNodeByID();
  const { idMap: boardActionsIDMap } = useBoardActions();
  const { idMap: boardEntityTypesIDMap } = useBoardEntityTypes();
  const { fitView } = useReactFlow();
  const { handleAddToNodeSelection } = useBoardNodesContext();
  // const throttle = useThrottle();

  // const throttledOnNodesChange = throttle(onNodesChange, 100);

  const getNodeByID = (nodeID: string) =>
    nodes.find((node) => node.id === nodeID);

  const addToReactFlowNodes = (addedNode: Node) => {
    setNodes((prevNodes) => [...prevNodes, addedNode]);
  };

  const removeFromReactFlowNodes = (nodeID: string) => {
    const newNodes = nodes.filter((node) => node.id !== nodeID);
    setNodes(newNodes);
  };

  const updateReactFlowNode = (updatedNode: Node) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) =>
        node.id === updatedNode.id
          ? {
              ...node,
              data: {
                ...node.data,
                boardNode: {
                  ...node.data.boardNode,
                  attributes: {
                    ...node.data.boardNode.attributes,
                    ...updatedNode.data.boardNode.attributes,
                  },
                  meta: {
                    ...node.data.boardNode.meta,
                    ...updatedNode.data.boardNode.meta,
                  },
                },
              },
            }
          : node
      )
    );
  };

  const onInitRePositionNode = () => {
    const updatedNodes = [...nodes]; // Copy the nodes array to avoid mutating the state directly

    // Iterate over all the nodes and check if they overlap with others
    updatedNodes.forEach((node, index) => {
      const existingNodes = updatedNodes.filter((_, i) => i !== index); // Exclude the current node

      // Check if the node overlaps with any existing node
      if (!isSafePosition(node.position, node, existingNodes)) {
        // If it overlaps, find a safe position for this node
        const newNode = findSafePosition(node, existingNodes);
        updatedNodes[index] = { ...node, position: newNode.position }; // Update the position of the node
      }
    });

    // Update the nodes state after checking and moving overlapping nodes
    setNodes(updatedNodes);
  };

  const setAllBoardNodes = (nodes: Node[]) => {
    setNodes(nodes);
  };

  const getSelectedNodes = React.useCallback(() => {
    const nodesList = nodes;
    const selectedNodes = nodesList.filter((node) => node?.selected);
    return selectedNodes;
  }, [nodes]);

  const resetSelection = () => {
    const newNodes = nodes.map((node) => {
      return { ...node, selected: false };
    });
    setNodes(newNodes);
  };

  const enableNodeDrag = (updatedNodes: Node[]) => {
    const newNodes = updatedNodes.map((node) => ({
      ...node,
      data: { ...node.data, draggable: true },
      draggable: true,
    }));
    setNodes(newNodes);
  };

  const deselectNode = (nodeID: string) => {
    const newNodes = nodes.map((node) => {
      if (node.id === nodeID) {
        return { ...node, selected: false };
      }
      return node;
    });
    setNodes(newNodes);
  };

  const getAllBoardNodes = React.useCallback(() => {
    return nodes;
  }, [nodes]);

  const handleNodeEvents = (event: BoardEvent) => {
    const boardNodeID = event.entityID;
    console.log('handleNodeEvents', boardNodeID);

    const changes = parseJSONCleaned<any>(event.changes)
      ?.properties as ExtendedBoardNode;
    const boardActionID = event.boardActionID;
    if (!boardActionID) {
      return;
    }
    const boardAction = boardActionsIDMap?.[boardActionID];
    if (!boardAction) {
      return;
    }
    console.log('boardAction', boardAction.attributes.name);
    switch (boardAction.attributes.name) {
      case 'Created':
        console.log('CreatedCreated', changes);
        getBoardNodeByID(boardNodeID).then((response) => {
          console.log('responseresponse', response);
          const nodeToAdd = findSafePosition(
            transformSparkBoardNodeToRectFlowNodes(response.data, false),
            getAllBoardNodes() || []
          );
          addToReactFlowNodes(nodeToAdd);
          setTimeout(() => {
            handleAddToNodeSelection(response.data.id);

            fitView({
              nodes: [{ id: response.data.id }],
              duration: 500,
              padding: FIFTY_PERCENT_PADDING,
            });
          }, 100);
        });
        // Handle create action
        break;
      case 'Updated':
        const node = getNodeByID(boardNodeID);
        if (!node) {
          return;
        }
        const updatedSparkBoardNode: Partial<BoardNodes> = Object.entries(
          changes
        ).reduce<Partial<BoardNodes>>((acc, [key, value]) => {
          console.log('boardEventsBoardEvents', key);
          if (key === 'content') {
            // need to update meta data
            //@ts-ignore
            console.log('boardEventsBoardEvents new value', value.new);
            acc.meta = {
              ...acc.meta,
              //@ts-ignore
              data: value.new,
            };
            return acc;
          }
          //  // need to update attributes
          //@ts-ignore
          acc.attributes = {
            ...acc.attributes,
            [key]: value.new,
          };
          return acc;
        }, node.data.boardNode);
        const updatedReactFlowNode = {
          ...node,
          data: {
            ...node.data,
            boardNode: updatedSparkBoardNode,
          },
        };
        updateReactFlowNode(updatedReactFlowNode);

        // Handle update action
        break;
      case 'Deleted':
        // Handle delete action
        break;
      case 'Undo':
        // Handle delete action
        break;
      case 'Redo':
        // Handle delete action
        break;
      default:
        console.warn(`Unknown board action: ${boardAction.attributes.name}`);
    }
  };
  const _handleBoardEvent = (events: BoardEvent) => {};

  const handleBoardEvent = (dataContent: string) => {
    try {
      const events: BoardEvent[] = JSON.parse(dataContent);

      events.forEach((event) => {
        const boardNodeEntityTypeID = event.boardEntityTypeID;

        const boardEntityType = boardEntityTypesIDMap?.[boardNodeEntityTypeID];
        if (!boardNodeEntityTypeID || !boardEntityType) {
          return;
        }
        console.log('boardEntityType', boardEntityType.attributes.name);
        switch (boardEntityType.attributes.name) {
          case 'Board':
            _handleBoardEvent(event);
            break;
          case 'Node':
            handleNodeEvents(event);
            break;

          default:
            console.warn(
              `Unknown board entity type: ${boardEntityType.attributes.name}`
            );
        }
      });
    } catch (err) {
      console.error('Error processing boardEvents:', err);
    }
  };

  return {
    getNodeByID,
    addToReactFlowNodes,
    updateReactFlowNode,
    removeFromReactFlowNodes,
    setAllBoardNodes,
    getSelectedNodes,
    onInitRePositionNode,
    resetSelection,
    deselectNode,
    handleBoardEvent,
    getAllBoardNodes,
    nodes,
    setNodes,
    onNodesChange: onNodesChange,
    enableNodeDrag,
  };
};
